import { Inject, Injectable, Optional } from '@angular/core';
import { State } from '@app/app.reducer';
import {
  ActionTypes,
  AddStyleToList,
  CreateStyle,
  GetAllStyles,
  LoadStyle,
  SaveEventStyles,
  SaveOperatorStyles,
  SaveSuccess,
  SetAllStyles,
  SetSelectedStyle,
  SetStyleId,
  UpdateStyle
} from '@app/colorizer/colorizer.actions';
import { StyleTemplateModel } from '@app/colorizer/colorizer.interface';
import { ColorizerService } from '@app/colorizer/colorizer.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { COLORIZER_DEBOUNCE_TIME } from '@shared/tokens';
import { CssSkinModel } from '@store/customization/customization.interfaces';
import { generateSkinCss } from '@store/customization/customization.styles';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap } from 'rxjs/operators';

@Injectable()
export class ColorizerEffect {
  loadAllStyles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetAllStyles>(ActionTypes.GET_ALL_STYLES),
      debounceTime(this.colorizerDebounceTime),
      switchMap(() => {
        return this.colorizerService.loadAllEventStyles().pipe(
          map((allStyles: StyleTemplateModel[]) => new SetAllStyles(allStyles)),
          catchError(() => of(new SetAllStyles([])))
        );
      })
    )
  );

  loadStyle$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadStyle>(ActionTypes.LOAD_STYLE),
      debounceTime(this.colorizerDebounceTime),
      switchMap((data: any) => {
        const styleId = data.payload;
        return this.colorizerService.loadEventStyle(styleId).pipe(
          map((styleProperties: any) => {
            const cssString = generateSkinCss(styleProperties.styles);
            const cssSkin: CssSkinModel = {
              priority: 5,
              css: cssString,
              variables: styleProperties.styles,
              title: styleProperties.title,
              id: styleId
            };

            this.store.dispatch(new SetStyleId(styleId));

            return new SetSelectedStyle(cssSkin);
          }),
          catchError(() => of(new SetSelectedStyle(null)))
        );
      })
    )
  );

  updateStyle$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateStyle>(ActionTypes.UPDATE_STYLE),
      debounceTime(this.colorizerDebounceTime),
      switchMap((data: any) => {
        return this.colorizerService
          .updateEventStyle({ styles: data.payload.styles, title: data.payload.title }, data.payload.styleId)
          .pipe(
            map((statusCode: number) => {
              return new SaveSuccess(true);
            }),
            catchError(() => of(new SaveSuccess(false)))
          );
      })
    )
  );

  saveEventStyles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveEventStyles>(ActionTypes.SAVE_EVENT_STYLES),
      debounceTime(this.colorizerDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        return this.colorizerService.saveEventStyles(data.payload).pipe(
          map((statusCode: number) => {
            return new SaveSuccess(true);
          }),
          catchError(() => of(new SaveSuccess(false)))
        );
      })
    )
  );

  createEventStyles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateStyle>(ActionTypes.CREATE_STYLE),
      debounceTime(this.colorizerDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        return this.colorizerService.createEventStyle(data.payload).pipe(
          map((responseData: any) => {
            this.store.dispatch(new AddStyleToList(responseData));

            return new SaveSuccess(true);
          }),
          catchError(() => of(new SaveSuccess(false)))
        );
      })
    )
  );

  saveOperatorStyles$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveOperatorStyles>(ActionTypes.SAVE_OPERATOR_STYLES),
      debounceTime(this.colorizerDebounceTime),
      switchMap((data: any) => {
        if (!data.payload) {
          return EMPTY;
        }

        return this.colorizerService.saveOperatorStyles(data.payload).pipe(
          map((statusCode: number) => new SaveSuccess(true)),
          catchError(() => of(new SaveSuccess(false)))
        );
      })
    )
  );

  constructor(
    @Optional() @Inject(COLORIZER_DEBOUNCE_TIME) private colorizerDebounceTime: number,
    private colorizerService: ColorizerService,
    private actions$: Actions,
    private store: Store<State>
  ) {}
}
